import { supabase } from '../supabaseClient';
import type { Contract, ContractStatus } from '@shared/types';

// Helper to transform database row to Contract object
function transformContract(row: any): Contract {
  return {
    id: row.id,
    title: row.title,
    clientName: row.client_name,
    clientId: row.client_id,
    status: row.status,
    templateId: row.template_id,
    templateName: row.contract_templates?.name,
    templateType: row.contract_templates?.template_type,
    executiveSummary: row.executive_summary,
    deliverables: row.deliverables || [],
    milestones: row.milestones || [],
    payments: row.payments || [],
    clauses: row.clauses || [],
    expirationDate: row.expiration_date,
    paymentScheduleType: row.payment_schedule_type,
    recurringInterval: row.recurring_interval,
    // SOW-specific fields
    serviceCost: row.service_cost,
    licenseDescription: row.license_description,
    licenseCost: row.license_cost,
    estimatedStartDate: row.estimated_start_date,
    projectDuration: row.project_duration,
    // License details
    licenseProduct: row.license_product,
    licenseTerms: row.license_terms,
    licenseUsers: row.license_users,
    licensePricingTier: row.license_pricing_tier,
    // Payment configuration
    paymentMilestoneCount: row.payment_milestone_count,
    useCustomPayments: row.use_custom_payments,
    // Paths and metadata
    templatePath: row.template_path,
    generatedPath: row.generated_path,
    signedPath: row.signed_path,
    metadata: row.metadata || {},
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// Helper to transform Contract object to database row
function toDbContract(contract: Partial<Contract>, userId?: string) {
  const dbContract: any = {};
  
  // Basic fields
  if (contract.title !== undefined) dbContract.title = contract.title;
  if (contract.clientName !== undefined) dbContract.client_name = contract.clientName;
  if (contract.clientId !== undefined) dbContract.client_id = contract.clientId;
  if (contract.status !== undefined) dbContract.status = contract.status;
  if (contract.templateId !== undefined) dbContract.template_id = contract.templateId;
  if (contract.executiveSummary !== undefined) dbContract.executive_summary = contract.executiveSummary;
  if (contract.expirationDate !== undefined) dbContract.expiration_date = contract.expirationDate;
  if (contract.paymentScheduleType !== undefined) dbContract.payment_schedule_type = contract.paymentScheduleType;
  if (contract.recurringInterval !== undefined) dbContract.recurring_interval = contract.recurringInterval;
  
  // JSONB fields
  if (contract.deliverables !== undefined) dbContract.deliverables = contract.deliverables;
  if (contract.milestones !== undefined) dbContract.milestones = contract.milestones;
  if (contract.payments !== undefined) dbContract.payments = contract.payments;
  if (contract.clauses !== undefined) dbContract.clauses = contract.clauses;
  if (contract.metadata !== undefined) dbContract.metadata = contract.metadata;
  
  // SOW-specific fields
  if (contract.serviceCost !== undefined) dbContract.service_cost = contract.serviceCost;
  if (contract.licenseDescription !== undefined) dbContract.license_description = contract.licenseDescription;
  if (contract.licenseCost !== undefined) dbContract.license_cost = contract.licenseCost;
  if (contract.estimatedStartDate !== undefined) dbContract.estimated_start_date = contract.estimatedStartDate;
  if (contract.projectDuration !== undefined) dbContract.project_duration = contract.projectDuration;
  
  // License details
  if (contract.licenseProduct !== undefined) dbContract.license_product = contract.licenseProduct;
  if (contract.licenseTerms !== undefined) dbContract.license_terms = contract.licenseTerms;
  if (contract.licenseUsers !== undefined) dbContract.license_users = contract.licenseUsers;
  if (contract.licensePricingTier !== undefined) dbContract.license_pricing_tier = contract.licensePricingTier;
  
  // Payment configuration
  if (contract.paymentMilestoneCount !== undefined) dbContract.payment_milestone_count = contract.paymentMilestoneCount;
  if (contract.useCustomPayments !== undefined) dbContract.use_custom_payments = contract.useCustomPayments;
  
  // Paths
  if (contract.templatePath !== undefined) dbContract.template_path = contract.templatePath;
  if (contract.generatedPath !== undefined) dbContract.generated_path = contract.generatedPath;
  if (contract.signedPath !== undefined) dbContract.signed_path = contract.signedPath;
  
  // User
  if (userId) dbContract.created_by = userId;
  
  return dbContract;
}

export const contractService = {
  async getContracts() {
    const { data, error } = await supabase
      .from('contracts')
      .select(`
        *,
        contract_templates (
          name,
          template_type
        )
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data ? data.map(transformContract) : [];
  },

  async getContract(id: string) {
    const { data, error } = await supabase
      .from('contracts')
      .select(`
        *,
        contract_templates (
          name,
          template_type
        )
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return transformContract(data);
  },

  async createContract(contract: Partial<Contract>) {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const dbContract = toDbContract(contract, user.id);

    const { data, error } = await supabase
      .from('contracts')
      .insert([dbContract])
      .select()
      .single();
    
    if (error) {
      console.error('Create contract error:', error);
      throw error;
    }
    return transformContract(data);
  },

  async updateContract(id: string, updates: Partial<Contract>) {
    const dbUpdates = toDbContract(updates);

    const { data, error } = await supabase
      .from('contracts')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return transformContract(data);
  },

  async deleteContract(id: string) {
    const { error } = await supabase
      .from('contracts')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async updateStatus(contractId: string, newStatus: ContractStatus) {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-status`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ contractId, newStatus }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update status');
    }

    return response.json();
  },

  async sendForSignature(contractId: string, signerEmail: string, signerName: string) {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-signature`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ contractId, signerEmail, signerName }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send for signature');
    }

    return response.json();
  },

  async emailForClientReview(params: {
    contractIds: string[];
    toContactIds: string[];
    subject?: string;
    bodyHtml?: string;
    additionalTemplateIds?: string[];
  }): Promise<{
    sent: true;
    contract_count: number;
    recipients: Array<{ id: string; email: string; name: string }>;
    attachments: string[];
    transitions: Array<{ id: string; status_before: string; status_after: string }>;
  }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-contract-review-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          contract_ids: params.contractIds,
          to_contact_ids: params.toContactIds,
          subject: params.subject,
          body_html: params.bodyHtml,
          additional_template_ids: params.additionalTemplateIds ?? [],
        }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || 'Failed to send contracts');
    }
    return response.json();
  },
};

